import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Contact from "../Navbar/Pages/Contact";
import "../css/billing.css";
import { BASE_URL } from "../../src/constants/constant";
import Cookies from "js-cookie";

const Billing = () => {
  const location = useLocation();

  const {
    selectedSeats,
    totalAmount,
    packageId,
    bustype,
    selectedEntryPoint,
    adults,
    children,
    rooms,
    bookingId, // Added bookingId from location state
  } = location.state || {
    selectedSeats: [],
    totalAmount: 0,
    packageId: null,
    bustype: null,
    selectedEntryPoint: "",
    adults: 0,
    children: 0,
    rooms: 0,
    bookingId: null,
  };

  const [isLocked, setIsLocked] = useState(false);
  const [lockError, setLockError] = useState(null);
  const userId = sessionStorage.getItem("user_id");

  const seatIds = selectedSeats.map((seat) => seat.id);

  // 🔓 Unlock Seats via fetch (for unmount/navigation)
  const unlockSeats = async () => {
    if (seatIds.length === 0 || !bookingId) return;

    try {
      const response = await fetch(`${BASE_URL}unlock_seat.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          seat_ids: seatIds,
          package_id: packageId,
          user_id: userId,
          booking_id: bookingId, // Include booking ID when unlocking
        }),
      });

      const data = await response.json();

      if (!data.success) {
        console.error("Failed to unlock seats:", data.message);
      }
    } catch (error) {
      console.error("Unlock Seats Error:", error);
    }
  };

  // 🔓 Unlock Seats via Beacon (for tab/browser close)
  const unlockSeatsViaBeacon = () => {
    if (seatIds.length === 0 || !bookingId) return;

    const payload = JSON.stringify({
      seat_ids: seatIds,
      package_id: packageId,
      user_id: userId,
      booking_id: bookingId, // Include booking ID when unlocking
    });

    const blob = new Blob([payload], { type: "application/json" });

    navigator.sendBeacon(`${BASE_URL}unlock_seat.php`, blob);
  };

  useEffect(() => {
    const verifyBooking = async () => {
      if (!bookingId) {
        setLockError("No booking ID found. Please start over.");
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}verify_booking.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            booking_id: bookingId,
            user_id: userId,
          }),
        });

        const data = await response.json();
        if (data.success) {
          setIsLocked(true);
        } else {
          setLockError(data.message || "Invalid booking. Please start over.");
        }
      } catch (error) {
        setLockError("Error verifying booking status.");
        console.error("Verify Booking Error:", error);
      }
    };

    verifyBooking();

    // Cleanup when user navigates back
    return () => {
      deleteBookingOnBack();
    };
  }, [bookingId, userId]);

  // 🔓 Unlock seats on tab close / browser close
  useEffect(() => {
    window.addEventListener("beforeunload", unlockSeatsViaBeacon);
    return () => {
      window.removeEventListener("beforeunload", unlockSeatsViaBeacon);
    };
  }, []);

  const deleteBookingOnBack = async () => {
    if (!bookingId || !userId) return;

    try {
      const response = await fetch(`${BASE_URL}delete_booking.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ booking_id: bookingId, user_id: userId }),
      });

      const data = await response.json();
      if (!data.success) {
        console.error("Failed to delete booking:", data.message);
      }
    } catch (error) {
      console.error("Delete Booking Error:", error);
    }
  };

  const handleBooking = async () => {
    if (!isLocked) {
      alert("Booking verification failed. Please try again.");
      return;
    }

    if (selectedSeats.length === 0) {
      alert("Please select at least one seat.");
      return;
    }

    if (!bookingId) {
      alert("Invalid booking reference. Please start over.");
      return;
    }

    try {
      const bookingDetails = {
        booking_id: bookingId, // Include booking ID in details
        user_id: userId,
        package_id: packageId,
        seat_id: seatIds,
        bus_id: bustype,
        payment_status: "pending",
        balance_payment: "pending",
        entry_point: selectedEntryPoint,
        adults,
        children,
        rooms,
      };

      Cookies.set("bookingDetails", JSON.stringify(bookingDetails), {
        expires: 1,
      });

      const response = await fetch(`${BASE_URL}phonepe.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          booking_id: bookingId,
          amount: totalAmount,
          user_id: userId,
        }),
      });

      const responseText = await response.text();
      console.log("Raw PhonePe PHP response:", responseText);

      let phonepeData;
      try {
        phonepeData = JSON.parse(responseText);
      } catch (jsonError) {
        throw new Error("Invalid JSON received from phonepe.php");
      }

      const redirectUrl = phonepeData.redirectUrl;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        throw new Error(
          phonepeData.error || "No redirectUrl received from phonepe.php"
        );
      }
    } catch (error) {
      console.error("Booking Error:", error.message);
      alert("An error occurred during payment initiation. Please try again.");
    }
  };

  return (
    <div>
      <Navbar />
      <div className="billing-container">
        <h2 className="billing-h2">Billing Details</h2>
        {lockError ? (
          <div className="error-message">Error: {lockError}</div>
        ) : (
          <div>
            <h3>Selected Seats:</h3>
            <ul className="seatsul">
              <li>
                {selectedSeats.map((seat) => `Seat ${seat.name}`).join(", ")}
              </li>
            </ul>

            <div className="guests-info">
              <p>Total Adults: {adults}</p>
              <p>Total Children: {children}</p>
              <p>Total Rooms: {rooms}</p>
            </div>
            <h3 className="total-amount">
              Total Advance Payment: ₹{totalAmount}
            </h3>

            <button
              className="make-payment-button"
              onClick={handleBooking}
              disabled={!isLocked}
            >
              {isLocked ? "Make Payment" : "Verifying Booking..."}
            </button>
          </div>
        )}
      </div>
      <Contact />
    </div>
  );
};

export default Billing;
